import { Resource } from "@/types/resources";

import { type MeteringReport } from "./MeteringReport";

/**
 * Families of compute resources, each listing every name under which one
 * quantity is metered.
 *
 * Each quantity is metered per substrate (`actions::fargate::…`,
 * `actions::microvm::…`), and months that closed before those names existed
 * report it under the retired `actions::…` name. A surface that labels a total
 * generically ("Invocations", "Run Time") must sum every member, or it omits
 * MicroVM runs and shows those older months as zero.
 *
 * A report can contain retired names, substrate-qualified names, or both (in
 * the month the substrate-qualified names were introduced). Summing is correct
 * in every case because each invocation is metered under exactly one name.
 */
export const INVOCATIONS_SCHEDULED_RESOURCES: readonly Resource[] = [
  Resource.RetiredInvocationsScheduled,
  Resource.FargateInvocationsScheduled,
  Resource.MicrovmInvocationsScheduled,
];

export const INVOCATION_RUNTIME_MILLIS_RESOURCES: readonly Resource[] = [
  Resource.RetiredInvocationRuntimeMillis,
  Resource.FargateInvocationRuntimeMillis,
  Resource.MicrovmInvocationRuntimeMillis,
];

export const INVOCATION_VCPU_MILLIS_RESOURCES: readonly Resource[] = [
  Resource.RetiredInvocationVCpuMillis,
  Resource.FargateInvocationVCpuMillis,
  Resource.MicrovmInvocationVCpuMillis,
];

export const INVOCATION_RAM_GB_MILLIS_RESOURCES: readonly Resource[] = [
  Resource.RetiredInvocationRamGBMillis,
  Resource.FargateInvocationRamGBMillis,
  Resource.MicrovmInvocationRamGBMillis,
];

/**
 * What the platform is billed for compute, per substrate, at the provider's
 * list rates. The retired names had no cost meter, so a month that closed
 * before the substrate-qualified names existed contributes nothing, and the
 * total under-reports that month's cost instead of estimating it.
 */
export const PROVIDER_COST_MICRODOLLARS_RESOURCES: readonly Resource[] = [
  Resource.FargateProviderCostMicrodollars,
  Resource.MicrovmProviderCostMicrodollars,
];

/**
 * Total one family of resources over a report.
 *
 * Absent members contribute zero: an org that has never run a MicroVM has no
 * MicroVM rows, which is not an error.
 */
export function sumMeteredResources(
  report: MeteringReport | null | undefined,
  family: readonly Resource[],
): number {
  if (!report) {
    return 0;
  }

  return report.values.reduce(
    (total, value) =>
      family.includes(value.resource_name) ? total + value.quantity : total,
    0,
  );
}
